import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import { toIsoDate, todayInZone } from '@/lib/timezone';
import {
  TIME_OFF_KINDS,
  WEEKDAYS,
  useSaveAvailability,
  useSaveTimeOff,
  type TechnicianAvailability,
  type TechnicianTimeOff,
  type TimeOffKind,
  type Weekday,
} from '@/routes/technicians/useAvailability';

/**
 * Create-or-edit one row of the weekly template (M1-39) — owner/admin only,
 * enforced server-side by `users.manage`.
 *
 * <b>Explicit `htmlFor`/`id` on every control, never a wrapping `<label>`.</b>
 * M1-37 found the reason the expensive way: a `<label>` wrapped around a
 * `<select>` folds every `<option>`'s text into the control's accessible name,
 * so the day field would announce as
 * "DaySundayMondayTuesdayWednesdayThursdayFridaySaturday" and
 * `getByRole('combobox', { name: 'Day of week' })` would simply not find it.
 * Hints sit outside the label as `aria-describedby` for the same reason.
 */
export function AvailabilityDialog({
  userId,
  timeZone,
  row,
  onClose,
  onSaved,
}: {
  userId: string;
  /** The account's own IANA zone, for seeding the effective-from default. See `todayIso`. */
  timeZone: string | undefined;
  /** Absent for create. */
  row?: TechnicianAvailability;
  onClose: () => void;
  onSaved: (announcement: string) => void;
}) {
  const { t } = useTranslation();
  const save = useSaveAvailability(userId, row?.id ?? null);

  const [day, setDay] = useState<Weekday>((row?.day as Weekday) ?? 'Monday');
  const [startTime, setStartTime] = useState(row?.startTime ?? '07:30');
  const [endTime, setEndTime] = useState(row?.endTime ?? '17:00');
  const [effectiveFrom, setEffectiveFrom] = useState(row?.effectiveFrom ?? todayIso(timeZone));
  const [effectiveTo, setEffectiveTo] = useState(row?.effectiveTo ?? '');

  // Compared as plain STRINGS, not Date objects: ISO `yyyy-MM-dd` and `HH:mm`
  // both sort lexicographically, and going through `new Date()` would drag the
  // viewer's timezone into a comparison that has nothing to do with one. The
  // same call M1-37's licence dialog makes for its two dates.
  const endBeforeStart = endTime <= startTime;
  const toBeforeFrom = effectiveTo !== '' && effectiveTo < effectiveFrom;
  const invalid = endBeforeStart || toBeforeFrom;

  return (
    <Dialog
      titleId="availability-dialog-title"
      title={row ? t('technicianAvailability.addDay.editTitle') : t('technicianAvailability.addDay.title')}
      onClose={onClose}
      testId="availability-dialog"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={invalid || save.isPending}
            onClick={() =>
              save.mutate(
                {
                  day,
                  startTime,
                  endTime,
                  effectiveFrom,
                  effectiveTo: effectiveTo === '' ? null : effectiveTo,
                },
                { onSuccess: () => onSaved(t('technicianAvailability.addDay.done')) },
              )
            }
          >
            {t('technicianAvailability.addDay.save')}
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        <div>
          <label htmlFor="availability-day" className="text-sm font-medium">
            {t('technicianAvailability.addDay.dayLabel')}
          </label>
          <select
            id="availability-day"
            className={FIELD_CLASS}
            value={day}
            onChange={(event) => setDay(event.target.value as Weekday)}
          >
            {WEEKDAYS.map((weekday) => (
              <option key={weekday} value={weekday}>
                {t(`technicianAvailability.day.${weekday}`)}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="availability-start" className="text-sm font-medium">
              {t('technicianAvailability.addDay.startLabel')}
            </label>
            {/* A real `<input type="time">`: keyboard-operable, localised and
                screen-reader-labelled by the platform, the same reasoning
                BoardDateNav gives for using a native date input rather than a
                bespoke picker. It also emits the zero-padded "HH:mm" the API's
                regex and its check constraint both require — an unpadded
                "7:30" would be refused, and the constraint's ordering rule
                depends on that padding. */}
            <input
              id="availability-start"
              type="time"
              className={FIELD_CLASS}
              value={startTime}
              onChange={(event) => setStartTime(event.target.value)}
              aria-describedby="availability-time-hint"
            />
          </div>
          <div>
            <label htmlFor="availability-end" className="text-sm font-medium">
              {t('technicianAvailability.addDay.endLabel')}
            </label>
            <input
              id="availability-end"
              type="time"
              className={FIELD_CLASS}
              value={endTime}
              onChange={(event) => setEndTime(event.target.value)}
              aria-describedby="availability-time-hint"
            />
          </div>
        </div>
        <p id="availability-time-hint" className="text-xs text-[var(--color-muted-foreground)]">
          {t('technicianAvailability.addDay.timeHint')}
        </p>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="availability-from" className="text-sm font-medium">
              {t('technicianAvailability.addDay.effectiveFromLabel')}
            </label>
            <input
              id="availability-from"
              type="date"
              className={FIELD_CLASS}
              value={effectiveFrom}
              onChange={(event) => setEffectiveFrom(event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="availability-to" className="text-sm font-medium">
              {t('technicianAvailability.addDay.effectiveToLabel')}
            </label>
            <input
              id="availability-to"
              type="date"
              className={FIELD_CLASS}
              value={effectiveTo}
              onChange={(event) => setEffectiveTo(event.target.value)}
              aria-describedby="availability-to-hint"
            />
            <p id="availability-to-hint" className="text-xs text-[var(--color-muted-foreground)]">
              {t('technicianAvailability.addDay.effectiveToHint')}
            </p>
          </div>
        </div>

        {save.isError ? (
          <p role="alert" className="text-sm text-[var(--color-destructive)]">
            {t('technicianAvailability.addDay.failed')}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

/** Create-or-edit one time-off entry (M1-39). Owner/admin only. */
export function TimeOffDialog({
  userId,
  timeZone,
  row,
  onClose,
  onSaved,
}: {
  userId: string;
  /** The account's own IANA zone, for seeding the date defaults. See `todayIso`. */
  timeZone: string | undefined;
  row?: TechnicianTimeOff;
  onClose: () => void;
  onSaved: (announcement: string) => void;
}) {
  const { t } = useTranslation();
  const save = useSaveTimeOff(userId, row?.id ?? null);

  const [kind, setKind] = useState<TimeOffKind>((row?.kind as TimeOffKind) ?? 'Pto');
  const [startDate, setStartDate] = useState(row?.startDate ?? todayIso(timeZone));
  const [endDate, setEndDate] = useState(row?.endDate ?? todayIso(timeZone));
  const [notes, setNotes] = useState(row?.notes ?? '');

  // `<`, not `<=`: both ends are inclusive, so one day off is the same date
  // twice and is the commonest entry there is. This mirrors the API's own
  // guard exactly rather than being a stricter client-side paraphrase of it.
  const invalid = endDate < startDate;

  return (
    <Dialog
      titleId="time-off-dialog-title"
      title={row ? t('technicianAvailability.addTimeOff.editTitle') : t('technicianAvailability.addTimeOff.title')}
      onClose={onClose}
      testId="time-off-dialog"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={invalid || save.isPending}
            onClick={() =>
              save.mutate(
                { kind, startDate, endDate, notes: notes.trim() === '' ? null : notes.trim() },
                { onSuccess: () => onSaved(t('technicianAvailability.addTimeOff.done')) },
              )
            }
          >
            {t('technicianAvailability.addTimeOff.save')}
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        <div>
          <label htmlFor="time-off-kind" className="text-sm font-medium">
            {t('technicianAvailability.addTimeOff.kindLabel')}
          </label>
          <select
            id="time-off-kind"
            className={FIELD_CLASS}
            value={kind}
            onChange={(event) => setKind(event.target.value as TimeOffKind)}
          >
            {TIME_OFF_KINDS.map((option) => (
              <option key={option} value={option}>
                {t(`technicianAvailability.kind.${option}`)}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="time-off-start" className="text-sm font-medium">
              {t('technicianAvailability.addTimeOff.startLabel')}
            </label>
            <input
              id="time-off-start"
              type="date"
              className={FIELD_CLASS}
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="time-off-end" className="text-sm font-medium">
              {t('technicianAvailability.addTimeOff.endLabel')}
            </label>
            <input
              id="time-off-end"
              type="date"
              className={FIELD_CLASS}
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              aria-describedby="time-off-end-hint"
            />
            <p id="time-off-end-hint" className="text-xs text-[var(--color-muted-foreground)]">
              {t('technicianAvailability.addTimeOff.endHint')}
            </p>
          </div>
        </div>

        <div>
          <label htmlFor="time-off-notes" className="text-sm font-medium">
            {t('technicianAvailability.addTimeOff.notesLabel')}
          </label>
          <textarea
            id="time-off-notes"
            className={FIELD_CLASS}
            rows={2}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </div>

        {save.isError ? (
          <p role="alert" className="text-sm text-[var(--color-destructive)]">
            {t('technicianAvailability.addTimeOff.failed')}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

/**
 * Today as `yyyy-MM-dd`, on the ACCOUNT's clock, for seeding a date field.
 *
 * ⚠️ <b>An earlier draft used the viewer's clock and called that safe because
 * "it is only a form default". It is not only a form default.</b> A default is
 * the value most submissions carry, and this one is stored and then compared
 * against the account-local date by `TechnicianAvailabilityCalendar`: a
 * back-office admin in New York booking "off today" at 00:30 for a Chicago
 * shop wrote 2026-09-15 while the board's today was still 2026-09-14, so the
 * technician was not greyed today and was greyed tomorrow, with no error
 * anywhere. `timezone.ts`'s own header names the reasoning that draft leaned
 * on — "a dispatcher sits in the office" — as the way you ship a board that is
 * quietly wrong for the one franchise across a state line.
 *
 * The fallback to the viewer's clock is for the case where `GET /account` has
 * not landed, which on this page means the user opened a dialog before the
 * shell's own cached query resolved. It is a last resort, not the design.
 */
function todayIso(timeZone: string | undefined): string {
  if (timeZone) {
    return toIsoDate(todayInZone(timeZone));
  }
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}
